import React from 'react';
import styled from 'styled-components';
import { bool, string, oneOf } from 'prop-types';
import { transparentize } from 'polished';
import { variant } from 'styled-system';

import { inputPropTypesObject } from '../elements/form/decorators';
import LabelMessage from './LabelMessage';

const CheckboxContainer = styled('label')(
  ({ theme }) => ({
    display: 'inline-block',
    padding: '4px 8px',
    verticalAlign: 'middle',
    fontWeight: 700,
    transition: 'background .2s ease',
    position: 'relative',
    cursor: 'pointer',
    borderRadius: theme.radii[1],
    '&:hover': {
      background: transparentize(0.88, theme.colors.main)
    },
    [`&:hover ${StyledCheckbox}`]: {
      borderColor: theme.colors.main
    },
    [`&:active ${StyledCheckbox}`]: {
      transform: 'scale(.9)'
    }
  }),
  ({ theme }) =>
    variant({
      prop: 'type',
      variants: {
        success: {
          '&:hover': {
            background: transparentize(0.88, theme.colors.blue)
          },
          [`&:hover ${StyledCheckbox}`]: {
            borderColor: theme.colors.blue
          }
        },
        complete: {
          '&:hover': {
            background: transparentize(0.88, theme.colors.main)
          },
          [`&:hover ${StyledCheckbox}`]: {
            borderColor: theme.colors.main
          }
        },
        error: {
          '&:hover': {
            background: transparentize(0.88, theme.colors.red)
          },
          [`&:hover ${StyledCheckbox}`]: {
            borderColor: theme.colors.red
          }
        },
        warning: {
          '&:hover': {
            background: transparentize(0.88, theme.colors.secondary)
          },
          [`&:hover ${StyledCheckbox}`]: {
            borderColor: theme.colors.secondary
          },
          [`&:active ${StyledCheckbox}`]: {
            transform: 'scale(.9)'
          }
        },
        default: {
          '&:hover': {
            background: transparentize(0.88, theme.colors.grey)
          },
          [`&:hover ${StyledCheckbox}`]: {
            borderColor: theme.colors.grey
          },
          [`&:active ${StyledCheckbox}`]: {
            transform: 'scale(.9)'
          }
        }
      }
    })
);

const Icon = styled('svg')(() => ({
  fill: 'none',
  stroke: 'white',
  strokeWidth: '3px',
  strokeLinecap: 'round'
}));

const HiddenCheckbox = styled('input')(
  () => ({
    border: 0,
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: '1px'
  }),
  ({ theme }) =>
    variant({
      variants: {
        success: {
          '&:focus + div.styled-checkbox': {
            borderColor: theme.colors.blue
          },
          ':focus + div.styled-checkbox:before': {
            background: transparentize(0.8, theme.colors.blue)
          },
          ':checked + div.styled-checkbox': {
            background: theme.colors.blue,
            borderColor: theme.colors.blue
          }
        },
        complete: {
          '&:focus + div.styled-checkbox': {
            borderColor: theme.colors.main
          },
          ':focus + div.styled-checkbox:before': {
            background: transparentize(0.8, theme.colors.main)
          },
          ':checked + div.styled-checkbox': {
            background: theme.colors.main,
            borderColor: theme.colors.main
          }
        },
        error: {
          '&:focus + div.styled-checkbox': {
            borderColor: theme.colors.red
          },
          ':focus + div.styled-checkbox:before': {
            background: transparentize(0.8, theme.colors.red)
          },
          ':checked + div.styled-checkbox': {
            background: theme.colors.red,
            borderColor: theme.colors.red
          }
        },
        warning: {
          '&:focus + div.styled-checkbox': {
            borderColor: theme.colors.secondary
          },
          ':focus + div.styled-checkbox:before': {
            background: transparentize(0.8, theme.colors.secondary)
          },
          ':checked + div.styled-checkbox': {
            background: theme.colors.secondary,
            borderColor: theme.colors.secondary
          }
        },
        default: {
          '&:focus + div.styled-checkbox': {
            borderColor: theme.colors.grey
          },
          ':focus + div.styled-checkbox:before': {
            background: transparentize(0.8, theme.colors.grey)
          },
          ':checked + div.styled-checkbox': {
            background: theme.colors.grey,
            borderColor: theme.colors.grey
          }
        }
      }
    })
);

const StyledCheckbox = styled('div')(({ theme, error }) => ({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  marginRight: '8px',
  background: `${error ? transparentize(0.8, theme.colors.red) : 'transparent'}`,
  border: `solid 2px ${error ? theme.colors.red : theme.colors.grey}`,
  borderRadius: theme.radii[0],
  verticalAlign: 'middle',
  cursor: 'pointer',
  transition: 'transform .3s cubic-bezier(0.175, 0.885, 0.32, 1.275), background .15s, border .2s',
  '&:before': {
    content: '""',
    transition: 'background .3s ease',
    position: 'absolute',
    zIndex: -1,
    background: 'transparent',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: theme.radii[1]
  },
  [Icon]: {
    visibility: 'hidden'
  },
  [`${HiddenCheckbox}:focus + &`]: {
    borderColor: theme.colors.main
  },
  [`${HiddenCheckbox}:focus + &:before`]: {
    background: transparentize(0.8, theme.colors.main)
  },
  [`${HiddenCheckbox}:checked + &`]: {
    background: theme.colors.main,
    borderColor: theme.colors.main
  },
  [`${HiddenCheckbox}:checked + & ${Icon}`]: {
    visibility: 'visible'
  }
}));

const StyledLabel = styled('span')(
  () => ({
    verticalAlign: 'middle'
  }),
  ({ theme }) =>
    variant({
      prop: 'type',
      variants: {
        success: {
          color: theme.colors.blue
        },
        complete: {
          color: theme.colors.main
        },
        error: {
          color: theme.colors.red
        },
        warning: {
          color: theme.colors.secondary
        },
        default: {
          color: theme.colors.grey
        }
      }
    })
);

const Checkbox = React.forwardRef(({ label, messageWithIcon, errorMessage, defaultValue, type, ...input }, ref) => {
  const hasError = !!errorMessage;
  return (
    <CheckboxContainer type={type}>
      <HiddenCheckbox type="checkbox" variant={type} defaultChecked={defaultValue} {...input} ref={ref} />
      <StyledCheckbox error={hasError} className={'styled-checkbox'}>
        <Icon viewBox="0 0 24 24">
          <polyline points="18 8 11 16 7 12" />
        </Icon>
      </StyledCheckbox>
      <StyledLabel type={type}>{label}</StyledLabel>
      {errorMessage && (
        <LabelMessage withIcon={messageWithIcon} type="error" inputName={input.name}>
          {errorMessage}
        </LabelMessage>
      )}
    </CheckboxContainer>
  );
});

Checkbox.defaultProps = {
  label: '',
  errorMessage: '',
  messageWithIcon: false,
  type: ''
};

Checkbox.propTypes = {
  ...inputPropTypesObject,
  errorMessage: string,
  label: string.isRequired,
  messageWithIcon: bool,
  type: oneOf(['', 'success', 'complete', 'error', 'warning', 'default'])
};

export default Checkbox;
